import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { nanoid } from 'nanoid';

interface AuthUser {
  id: string;
  email: string;
  name: string;
}

interface WaitlistState {
  remainingSpots: number;
  isUserWaitlisted: boolean;
  waitlistPosition: number | null;
  waitlistedPUID: string | null;
  waitlistedEmail: string | null;
  joinedAt: string | null;
  authUser: AuthUser | null;
  authChecked: boolean;
  decrementSpots: () => void;
  addToWaitlist: (email: string) => number;
  isEmailWaitlisted: (email: string) => boolean;
  resetSpots: (count?: number) => void;
  clearWaitlistStatus: () => void;
  setAuthUser: (user: AuthUser | null) => void;
  setAuthChecked: (checked: boolean) => void;
}

export const useWaitlistStore = create<WaitlistState>()(
  persist(
    (set, get) => ({
      remainingSpots: 90,
      isUserWaitlisted: false,
      waitlistPosition: null,
      waitlistedPUID: null,
      waitlistedEmail: null,
      joinedAt: null,
      authUser: null,
      authChecked: false,

      decrementSpots: () => set((state) => ({
        remainingSpots: Math.max(1, state.remainingSpots - 1)
      })),

      addToWaitlist: (email: string) => {
        const position = 100 - get().remainingSpots + 1;
        set({
          isUserWaitlisted: true,
          waitlistedEmail: email,
          waitlistPosition: position,
          waitlistedPUID: nanoid(),
          joinedAt: new Date().toISOString()
        });
        return position;
      },

      isEmailWaitlisted: (email: string) => {
        return get().waitlistedEmail === email;
      },

      resetSpots: (count = 90) => set({ remainingSpots: count }),

      clearWaitlistStatus: () => set({
        isUserWaitlisted: false,
        waitlistPosition: null,
        waitlistedPUID: null,
        waitlistedEmail: null,
        joinedAt: null
      }),

      setAuthUser: (user: AuthUser | null) => set({ authUser: user }),

      setAuthChecked: (checked: boolean) => set({ authChecked: checked }),
    }),
    {
      name: 'meduler-plus-waitlist',
      partialize: (state) => ({
        remainingSpots: state.remainingSpots,
        isUserWaitlisted: state.isUserWaitlisted,
        waitlistPosition: state.waitlistPosition,
        waitlistedPUID: state.waitlistedPUID,
        waitlistedEmail: state.waitlistedEmail,
        joinedAt: state.joinedAt,
      }),
    }
  )
);
